import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { X, Save, User } from 'lucide-react';
import { Site, apiClient } from '../../services/api';
import { validate } from '../../utils/validation';

export interface Driver {
    id: string;
    firstName: string;
    lastName: string;
    licenceNumber: string;
    email: string;
    dateOfBirth: string;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    postcode?: string;
    phone?: string;
    siteId?: string | null;
    site?: Site | null;
    riskLevel?: 'low' | 'medium' | 'high';
    lastLicenceCheck?: string;
    consentProvided: boolean;
    consentExpiry?: string;
    active: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface UpdateDriverRequest {
    firstName: string;
    lastName: string;
    licenceNumber: string;
    email: string;
    dateOfBirth: string;
    phone?: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    postcode: string;
    siteId?: string;
}

interface EditDriverModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (driverId: string, driver: UpdateDriverRequest) => Promise<void>;
    isUpdating: boolean;
    driver: Driver | null;
}

const EditDriverModal: React.FC<EditDriverModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isUpdating,
    driver,
}) => {
    const [formData, setFormData] = useState<UpdateDriverRequest>({
        firstName: '',
        lastName: '',
        licenceNumber: '',
        email: '',
        dateOfBirth: '',
        addressLine1: '',
        addressLine2: '',
        phone: '',
        city: '',
        postcode: '',
        siteId: '',
    });

    const [errors, setErrors] = useState<Partial<UpdateDriverRequest>>({});
    const [sites, setSites] = useState<Site[]>([]);
    const [sitesLoading, setSitesLoading] = useState(false);

    // Load sites when modal opens
    useEffect(() => {
        if (isOpen) {
            loadSites();
        }
    }, [isOpen]);

    const loadSites = async () => {
        try {
            setSitesLoading(true);
            const response = await apiClient.getSites({ active: true, pageSize: 1000 });
            setSites(response.data);
        } catch (err) {
            console.error('Error loading sites:', err);
        } finally {
            setSitesLoading(false);
        }
    };

    // Populate form when driver prop changes
    useEffect(() => {
        if (driver) {
            setFormData({
                firstName: driver.firstName || '',
                lastName: driver.lastName || '',
                licenceNumber: driver.licenceNumber || '',
                email: driver.email || '',
                dateOfBirth: driver.dateOfBirth || '',
                addressLine1: driver.addressLine1 || '',
                addressLine2: driver.addressLine2 || '',
                phone: driver.phone || '',
                city: driver.city || '',
                postcode: driver.postcode || '',
                siteId: driver.siteId || '',
            });
            setErrors({});
        }
    }, [driver]);

    const validateForm = (): boolean => {
        const newErrors: Partial<UpdateDriverRequest> = {};

        newErrors.firstName = validate.required(formData.firstName, 'First name') ?? undefined;
        newErrors.lastName = validate.required(formData.lastName, 'Last name') ?? undefined;
        newErrors.licenceNumber =
            (validate.required(formData.licenceNumber, 'Licence number') ??
            validate.ukLicenceNumber(formData.licenceNumber)) ?? undefined;
        newErrors.email =
            (validate.required(formData.email, 'Email address') ??
            validate.email(formData.email)) ?? undefined;
        newErrors.dateOfBirth =
            (validate.required(formData.dateOfBirth, 'Date of birth') ??
            validate.dateOfBirth(formData.dateOfBirth)) ?? undefined;
        newErrors.phone = validate.ukPhone(formData.phone ?? '') ?? undefined;
        newErrors.addressLine1 = validate.required(formData.addressLine1, 'Address line 1') ?? undefined;
        newErrors.city = validate.required(formData.city, 'City') ?? undefined;
        newErrors.postcode =
            (validate.required(formData.postcode, 'Postcode') ??
            validate.ukPostcode(formData.postcode)) ?? undefined;

        // Remove undefined keys so Object.keys count is accurate
        Object.keys(newErrors).forEach(k => {
            if (newErrors[k as keyof typeof newErrors] === undefined) {
                delete newErrors[k as keyof typeof newErrors];
            }
        });

        setErrors(newErrors);

        // Show toast if validation fails to alert user
        if (Object.keys(newErrors).length > 0) {
            const errorFields = Object.keys(newErrors).join(', ');
            toast.error(`Please fix validation errors: ${errorFields}`, { duration: 4000 });
        }

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!driver) return;

        if (!validateForm()) {
            return;
        }

        try {
            // Convert empty siteId to undefined (backend will handle as null)
            const dataToSubmit = {
                ...formData,
                siteId: formData.siteId && formData.siteId.trim() !== '' ? formData.siteId : undefined
            };

            await onSubmit(driver.id, dataToSubmit);
            toast.success('Driver updated successfully');
            onClose();
        } catch (error: any) {
            // Display error to user with full details
            const errorMessage = error?.message || error?.error || 'Failed to update driver';
            toast.error(`Error updating driver: ${errorMessage}`, {
                duration: 6000,
            });
        }
    };

    const handleInputChange = (field: keyof UpdateDriverRequest, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const formatLicenceNumber = (value: string) => {
        return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    };

    const formatPostcode = (value: string) => {
        return value.toUpperCase().replace(/[^A-Z0-9\s]/g, '');
    };

    if (!isOpen || !driver) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                            <User className="w-6 h-6" style={{ color: '#69BE28' }} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Edit Driver</h2>
                            <p className="text-sm text-gray-500">
                                Update {driver.firstName} {driver.lastName}'s information
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={isUpdating}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Personal Information */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mb-3">Personal Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                                <input
                                    type="text"
                                    value={formData.firstName}
                                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    disabled={isUpdating}
                                />
                                {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                                <input
                                    type="text"
                                    value={formData.lastName}
                                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    disabled={isUpdating}
                                />
                                {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => handleInputChange('email', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    disabled={isUpdating}
                                    placeholder="john.doe@company.com"
                                />
                                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => handleInputChange('phone', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    disabled={isUpdating}
                                    placeholder="01234 567890"
                                />
                                {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                                <input
                                    type="date"
                                    value={formData.dateOfBirth}
                                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                                    className={`w-full md:w-1/2 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 ${errors.dateOfBirth ? 'border-red-500' : 'border-gray-300'}`}
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    disabled={isUpdating}
                                />
                                {errors.dateOfBirth && <p className="mt-1 text-sm text-red-600">{errors.dateOfBirth}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Licence Information */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mb-3">Licence Information</h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                UK Driving Licence Number *
                            </label>
                            <input
                                type="text"
                                value={formData.licenceNumber}
                                onChange={(e) => handleInputChange('licenceNumber', formatLicenceNumber(e.target.value))}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 font-mono ${errors.licenceNumber ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                disabled={isUpdating}
                                placeholder="ABCDE123456AB1CD"
                                maxLength={16}
                            />
                            {errors.licenceNumber && (
                                <p className="mt-1 text-sm text-red-600">{errors.licenceNumber}</p>
                            )}
                            <p className="mt-1 text-xs text-gray-500">
                                Enter the 16-character UK driving licence number
                            </p>
                        </div>
                    </div>

                    {/* Address Information */}
                    <div>
                        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider pb-2 border-b border-gray-200 mb-3">Address Information</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 1 *
                                </label>
                                <input
                                    type="text"
                                    value={formData.addressLine1}
                                    onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 ${errors.addressLine1 ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    disabled={isUpdating}
                                    placeholder="123 High Street"
                                />
                                {errors.addressLine1 && (
                                    <p className="mt-1 text-sm text-red-600">{errors.addressLine1}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Address Line 2
                                </label>
                                <input
                                    type="text"
                                    value={formData.addressLine2}
                                    onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    disabled={isUpdating}
                                    placeholder="Apartment, suite, etc. (optional)"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        City *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.city}
                                        onChange={(e) => handleInputChange('city', e.target.value)}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 ${errors.city ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                        disabled={isUpdating}
                                        placeholder="London"
                                    />
                                    {errors.city && (
                                        <p className="mt-1 text-sm text-red-600">{errors.city}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Postcode *
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.postcode}
                                        onChange={(e) => handleInputChange('postcode', formatPostcode(e.target.value))}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-offset-2 ${errors.postcode ? 'border-red-500' : 'border-gray-300'
                                            }`}
                                        style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                        disabled={isUpdating}
                                        placeholder="SW1A 1AA"
                                    />
                                    {errors.postcode && (
                                        <p className="mt-1 text-sm text-red-600">{errors.postcode}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Site/Location
                                </label>
                                <select
                                    value={formData.siteId || ''}
                                    onChange={(e) => handleInputChange('siteId', e.target.value || '')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-offset-2"
                                    style={{ '--tw-ring-color': 'rgba(105, 190, 40, 0.5)' } as React.CSSProperties}
                                    disabled={isUpdating || sitesLoading}
                                >
                                    <option value="">No Site Assigned</option>
                                    {sites.map(site => (
                                        <option key={site.id} value={site.id}>
                                            {site.name} ({site.code})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isUpdating}
                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                            style={{ backgroundColor: '#69BE28' }}                        >
                            {isUpdating ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    <span>Updating...</span>
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    <span>Update Driver</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditDriverModal;